import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { usePermissions } from "../hooks/usePermissions";
import {
    Lightbulb, Plus, X, Loader2, Inbox, MessageSquare, Archive as ArchiveIcon,
    Rocket, Filter, FileText, Link as LinkIcon, Paperclip, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import FileDropzone, { formatBytes, buildFileDownloadUrl } from "../components/FileDropzone";

/**
 * Build Prompt #18 / #19 — Client-side URL normalizer.
 * Auto-prepends https:// to bare domains so legacy DB entries (and user input
 * before a future BP18 build was deployed) never render as relative links
 * (which the router would resolve under our own domain). Module-scoped so any
 * card/modal can call it.
 */
function normalizeUrlClient(raw) {
    if (!raw || typeof raw !== "string") return raw;
    let url = raw.trim();
    if (!url) return url;
    const lower = url.toLowerCase();
    if (!(lower.startsWith("http://") || lower.startsWith("https://"))) {
        if (url.includes("://")) return url; // non-http scheme — leave for backend reject
        url = "https://" + url;
    }
    return url;
}

const STATUSES = ["Submitted", "Under Review", "More Info Needed", "Approved", "Archived"];
// Build Prompt #16 — by default the Concept Intake hides Approved + Archived ideas
// from the main list (they're considered "persistence" — historic, not actionable).
const ACTIVE_STATUSES = ["Submitted", "Under Review", "More Info Needed"];
const PRODUCT_TYPES = ["In-House", "Third-Party", "Bundled"];
const RISK_TIERS = ["Low", "Medium", "High", "Critical"];

export default function ConceptIntake() {
    const perm = usePermissions();
    const [ideas, setIdeas] = useState([]);
    const [controlModes, setControlModes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Build Prompt #16 — default view = "Active" (hides Approved + Archived).
    const [statusFilter, setStatusFilter] = useState("Active");
    // Build Prompt #14 — Innovation Pool is visible to ALL users by default;
    // toggle "My ideas only" filters to own.
    const [mineOnly, setMineOnly] = useState(false);
    // Build Prompt #16 — clicking a card opens the full Concept Detail modal.
    const [detailTarget, setDetailTarget] = useState(null);

    // modals
    const [submitOpen, setSubmitOpen] = useState(false);
    const [submitForm, setSubmitForm] = useState({ title: "", description: "", mission_impact: "", external_links: "", file_attachments: [] });
    const [actionTarget, setActionTarget] = useState(null); // { kind: "info"|"archive"|"promote", idea }
    const [actionNote, setActionNote] = useState("");
    const [promoteForm, setPromoteForm] = useState({ product_type: "In-House", risk_tier: "Medium", control_mode_id: "" });
    const [busy, setBusy] = useState(false);
    const [modalErr, setModalErr] = useState("");

    const refresh = async () => {
        try {
            const params = mineOnly ? "?mine=true" : "";
            const [i, m] = await Promise.all([
                api.get(`/product-ideas${params}`).then((r) => r.data),
                perm.reviewIdeas
                    ? api.get("/control-modes").then((r) => r.data).catch(() => [])
                    : Promise.resolve([]),
            ]);
            setIdeas(i);
            setControlModes(m);
        } catch (err) {
            const status = err?.response?.status;
            if (status !== 401) setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mineOnly]);

    const filteredIdeas = useMemo(() => {
        if (statusFilter === "All") return ideas;
        if (statusFilter === "Active") return ideas.filter((i) => ACTIVE_STATUSES.includes(i.idea_status));
        return ideas.filter((i) => i.idea_status === statusFilter);
    }, [ideas, statusFilter]);

    const stats = useMemo(() => {
        const by = Object.fromEntries(STATUSES.map((s) => [s, 0]));
        ideas.forEach((i) => { by[i.idea_status] = (by[i.idea_status] || 0) + 1; });
        return { ...by, total: ideas.length };
    }, [ideas]);

    // ---- Submit Idea ----
    const openSubmit = () => {
        setSubmitForm({ title: "", description: "", mission_impact: "", external_links: "", file_attachments: [] });
        setSubmitOpen(true);
        setModalErr("");
    };
    const submitIdea = async (e) => {
        e.preventDefault();
        if (submitForm.title.trim().length < 3) return setModalErr("Title must be at least 3 characters.");
        if (submitForm.description.trim().length < 10) return setModalErr("Description must be at least 10 characters.");
        if (submitForm.mission_impact.trim().length < 10) return setModalErr("Mission impact must be at least 10 characters.");
        setBusy(true);
        setModalErr("");
        try {
            // Build Prompt #18 — protocol enforcement: auto-prepend https:// to bare domains.
            const external_links = submitForm.external_links
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
                .map(normalizeUrlClient);
            const { data } = await api.post("/product-ideas", {
                title: submitForm.title,
                description: submitForm.description,
                mission_impact: submitForm.mission_impact,
                external_links,
                file_attachments: submitForm.file_attachments,
            });
            const fileMsg = submitForm.file_attachments.length
                ? ` with ${submitForm.file_attachments.length} file(s)`
                : "";
            toast.success(`${data.idea_id} submitted${fileMsg} — leadership has been notified.`);
            setSubmitOpen(false);
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setBusy(false);
        }
    };

    // ---- Action handlers (admin/exec) ----
    const openAction = (kind, idea) => {
        setActionTarget({ kind, idea });
        setActionNote("");
        setModalErr("");
        if (kind === "promote") {
            setPromoteForm({
                product_type: "In-House",
                risk_tier: "Medium",
                control_mode_id: controlModes[0]?.id || "",
            });
        }
    };
    const closeAction = () => { setActionTarget(null); setActionNote(""); };

    const submitAction = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!actionTarget) return;
        const trimmed = actionNote.trim();
        if (actionTarget.kind !== "promote" && trimmed.length < 3) {
            setModalErr("Note is required (min 3 characters).");
            return;
        }
        if (actionTarget.kind === "promote" && !promoteForm.control_mode_id) {
            setModalErr("Please select a control mode for the new product.");
            return;
        }
        setBusy(true);
        setModalErr("");
        try {
            const id = actionTarget.idea.idea_id;
            if (actionTarget.kind === "info") {
                await api.post(`/product-ideas/${id}/request-info`, { note: trimmed });
                toast.success(`Info requested from ${actionTarget.idea.submitter_email}`);
            } else if (actionTarget.kind === "archive") {
                await api.post(`/product-ideas/${id}/archive`, { note: trimmed });
                toast.success(`${id} archived`);
            } else if (actionTarget.kind === "promote") {
                const { data } = await api.post(`/product-ideas/${id}/promote`, promoteForm);
                toast.success(`${id} promoted — product '${actionTarget.idea.title}' created.`);
                if (data.promoted_product_id) {
                    setTimeout(() => {
                        // Subtle: deep link to the new product so reviewer can validate gates.
                        window.location.assign(`/products/${data.promoted_product_id}`);
                    }, 600);
                }
            }
            closeAction();
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" data-testid="concept-intake-loading">
                <div className="overline">Innovation / Concept Intake</div>
                <div className="heading-display text-4xl">Concept Intake</div>
                <div className="text-snow-dim font-mono text-sm">Loading ideas…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="border border-red-500/40 bg-red-500/5 p-6" data-testid="concept-intake-error">
                <div className="overline text-red-400">Concept Intake Unavailable</div>
                <div className="heading-display text-xl mt-2">Could not load idea queue</div>
                <div className="text-xs text-snow-dim mt-2 font-mono">{error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="concept-intake-page">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Innovation / Concept Intake</div>
                    <h1 className="heading-display text-4xl mt-1 flex items-center gap-3">
                        <Lightbulb className="w-8 h-8 text-signal" />
                        Concept Intake
                    </h1>
                    <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                        Idea funnel for new products. Submit a concept and leadership will triage it.
                        Promoted ideas land in the <Link to="/products" className="text-signal hover:underline">Products registry</Link> with full governance attached.
                    </p>
                </div>
                <button
                    data-testid="submit-idea-btn"
                    onClick={openSubmit}
                    disabled={!perm.submitIdea}
                    title={perm.submitIdea ? "Pitch a concept for leadership review" : "Viewers cannot submit ideas."}
                    className={`px-5 py-2 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2 transition-colors ${
                        perm.submitIdea
                            ? "bg-signal text-jet hover:bg-signal-hover"
                            : "bg-[#2A2A2A] text-snow-muted cursor-not-allowed"
                    }`}
                >
                    <Plus className="w-3 h-3" /> Submit Idea
                </button>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KPI testid="ci-kpi-submitted" label="Submitted" value={stats.Submitted} alert={stats.Submitted > 0} icon={Inbox} />
                <KPI testid="ci-kpi-under-review" label="Under Review" value={stats["Under Review"]} icon={MessageSquare} />
                <KPI testid="ci-kpi-info-needed" label="Info Needed" value={stats["More Info Needed"]} icon={MessageSquare} />
                <KPI testid="ci-kpi-approved" label="Approved" value={stats.Approved} icon={Rocket} />
                <KPI testid="ci-kpi-archived" label="Archived" value={stats.Archived} icon={ArchiveIcon} />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap" data-testid="ci-filter-bar">
                <Filter className="w-3 h-3 text-snow-muted" />
                <SegmentedControl name="status" options={["Active", "All", ...STATUSES]} value={statusFilter} onChange={setStatusFilter} />
                {perm.reviewIdeas && (
                    <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-snow-dim cursor-pointer" data-testid="mine-toggle-label">
                        <input
                            data-testid="mine-toggle"
                            type="checkbox"
                            checked={mineOnly}
                            onChange={(e) => setMineOnly(e.target.checked)}
                            className="accent-[#FF6A00]"
                        />
                        My ideas only
                    </label>
                )}
                <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider ml-auto">
                    Showing {filteredIdeas.length} of {ideas.length}
                </span>
            </div>

            {/* Ideas Grid (Standard Card layout per spec) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ideas-grid">
                {filteredIdeas.length === 0 ? (
                    <div className="border border-[#2A2A2A] bg-jet-elev1 col-span-full px-5 py-12 text-center text-snow-muted text-xs font-mono">
                        No ideas in this view yet. {perm.submitIdea && "Click Submit Idea above to start the funnel."}
                    </div>
                ) : filteredIdeas.map((idea) => (
                    <IdeaCard
                        key={idea.id}
                        idea={idea}
                        canReview={perm.reviewIdeas}
                        onAction={openAction}
                        onOpenDetail={setDetailTarget}
                    />
                ))}
            </div>

            {/* Concept Detail Modal — Build Prompt #16 */}
            {detailTarget && (
                <ConceptDetailModal
                    idea={detailTarget}
                    onClose={() => setDetailTarget(null)}
                    onAction={(kind, idea) => { setDetailTarget(null); openAction(kind, idea); }}
                    canReview={perm.reviewIdeas}
                    canAppendAssets={perm.appendConceptAssets}
                    isSubmitter={detailTarget.submitter_id === perm.user?.id}
                    onIdeaUpdated={(updated) => {
                        setIdeas((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
                        setDetailTarget(updated);
                    }}
                />
            )}

            {/* Submit modal */}
            {submitOpen && (
                <Modal title="Submit Concept" onClose={() => setSubmitOpen(false)}>
                    <form onSubmit={submitIdea} className="space-y-4" data-testid="submit-idea-form">
                        <Field label={<>Title <span className="text-signal">*</span></>}>
                            <input
                                data-testid="idea-title-input"
                                required
                                minLength={3}
                                maxLength={120}
                                value={submitForm.title}
                                onChange={(e) => setSubmitForm({ ...submitForm, title: e.target.value })}
                                className="cerb-input"
                                placeholder="Edge AI Threat Classifier"
                            />
                        </Field>
                        <Field label={<>Description <span className="text-signal">*</span></>}>
                            <textarea
                                data-testid="idea-description-input"
                                rows={4}
                                required
                                minLength={10}
                                maxLength={3000}
                                value={submitForm.description}
                                onChange={(e) => setSubmitForm({ ...submitForm, description: e.target.value })}
                                className="cerb-input"
                                placeholder="What is it? How would it work?"
                            />
                            <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider flex justify-between">
                                <span>10-3000 chars</span>
                                <span className="tabular-nums">{submitForm.description.length}/3000</span>
                            </div>
                        </Field>
                        <Field label={<>Mission Impact <span className="text-signal">*</span></>}>
                            <textarea
                                data-testid="idea-impact-input"
                                rows={3}
                                required
                                minLength={10}
                                maxLength={1500}
                                value={submitForm.mission_impact}
                                onChange={(e) => setSubmitForm({ ...submitForm, mission_impact: e.target.value })}
                                className="cerb-input"
                                placeholder="Why do we need this?"
                            />
                        </Field>
                        <Field label="File Attachments">
                            <FileDropzone
                                value={submitForm.file_attachments}
                                onChange={(next) => setSubmitForm({ ...submitForm, file_attachments: next })}
                                testIdPrefix="idea-files"
                                label="Drop spec sheets, screenshots, PDFs"
                                helpText="Up to 10 files · 25 MB each · 50 MB total"
                            />
                        </Field>
                        <Field label="External Links (comma-separated URLs)">
                            <input
                                data-testid="idea-external-links-input"
                                value={submitForm.external_links}
                                onChange={(e) => setSubmitForm({ ...submitForm, external_links: e.target.value })}
                                className="cerb-input"
                                placeholder="https://docs..., https://demo..."
                            />
                            <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider">
                                Optional · prototypes, public docs, design files
                            </div>
                        </Field>
                        {modalErr && <div className="text-red-400 text-xs font-mono" data-testid="submit-idea-error">{modalErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={() => setSubmitOpen(false)} className="cerb-btn-secondary" data-testid="submit-idea-cancel">Cancel</button>
                            <button type="submit" disabled={busy} className="cerb-btn-primary" data-testid="submit-idea-confirm">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Action modal (info / archive / promote) */}
            {actionTarget && (
                <Modal
                    title={
                        actionTarget.kind === "info" ? "Request More Info" :
                        actionTarget.kind === "archive" ? "Archive Idea" :
                        "Promote to Product"
                    }
                    onClose={closeAction}
                    accent={actionTarget.kind === "promote"}
                >
                    <form onSubmit={submitAction} className="space-y-4" data-testid={`action-modal-${actionTarget.kind}`}>
                        <div className="text-xs font-mono text-snow-dim">
                            <span className="text-signal font-bold tabular-nums">{actionTarget.idea.idea_id}</span>{" "}
                            <span className="text-snow">{actionTarget.idea.title}</span>
                        </div>
                        <div className="text-[11px] text-snow-muted font-mono italic border-l-2 border-signal/40 pl-3">
                            "{actionTarget.idea.description.slice(0, 240)}{actionTarget.idea.description.length > 240 ? "…" : ""}"
                        </div>

                        {actionTarget.kind === "promote" ? (
                            <>
                                <div className="border border-signal bg-signal/5 px-3 py-2 text-[11px] font-mono text-signal">
                                    <Rocket className="w-3 h-3 inline mr-1" />
                                    <span className="font-bold">CONVERSION:</span> a new entry in the products registry will be created with the title above. Gate instances will be initialized against the chosen control mode.
                                </div>
                                {/* File hand-off summary per Build Prompt #10 */}
                                {(actionTarget.idea.file_attachments?.length > 0) ? (
                                    <div className="border border-[#2A2A2A] bg-jet-elev2 p-3" data-testid="promote-files-summary">
                                        <div className="flex items-center gap-2 overline mb-2">
                                            <Paperclip className="w-3 h-3" />
                                            Files Carrying Over
                                            <span className="text-[10px] text-snow-muted ml-auto tabular-nums normal-case tracking-normal">
                                                {actionTarget.idea.file_attachments.length} files · {formatBytes(actionTarget.idea.file_attachments.reduce((s, f) => s + (f.size || 0), 0))}
                                            </span>
                                        </div>
                                        <ul className="text-[11px] font-mono space-y-1">
                                            {actionTarget.idea.file_attachments.map((f) => (
                                                <li key={f.file_id} className="flex items-center gap-2 text-snow-dim" data-testid={`promote-file-${f.file_id}`}>
                                                    <FileText className="w-3 h-3 text-snow-muted flex-shrink-0" />
                                                    <span className="truncate flex-1">{f.filename}</span>
                                                    <span className="text-[10px] text-snow-muted tabular-nums">{formatBytes(f.size)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="text-[10px] text-snow-muted mt-2 font-mono uppercase tracking-wider">
                                            → Will appear on the new product AND on Gate 00.
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-[11px] text-snow-muted font-mono italic" data-testid="promote-no-files">
                                        No files attached to this idea — only the metadata will be carried over.
                                    </div>
                                )}
                                <Field label="Product Type">
                                    <div className="grid grid-cols-3 gap-2" data-testid="promote-type-pills">
                                        {PRODUCT_TYPES.map((t) => (
                                            <button
                                                key={t}
                                                type="button"
                                                data-testid={`promote-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                                onClick={() => setPromoteForm({ ...promoteForm, product_type: t })}
                                                className={`border px-2 py-2 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                                                    promoteForm.product_type === t
                                                        ? "border-signal text-signal bg-signal/10"
                                                        : "border-[#2A2A2A] text-snow-dim hover:border-snow"
                                                }`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="Risk Tier">
                                    <div className="grid grid-cols-4 gap-2" data-testid="promote-risk-pills">
                                        {RISK_TIERS.map((r) => (
                                            <button
                                                key={r}
                                                type="button"
                                                data-testid={`promote-risk-${r.toLowerCase()}`}
                                                onClick={() => setPromoteForm({ ...promoteForm, risk_tier: r })}
                                                className={`border px-2 py-2 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                                                    promoteForm.risk_tier === r
                                                        ? "border-signal text-signal bg-signal/10"
                                                        : "border-[#2A2A2A] text-snow-dim hover:border-snow"
                                                }`}
                                            >
                                                {r}
                                            </button>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="Control Mode">
                                    <select
                                        data-testid="promote-mode-select"
                                        required
                                        value={promoteForm.control_mode_id}
                                        onChange={(e) => setPromoteForm({ ...promoteForm, control_mode_id: e.target.value })}
                                        className="cerb-input"
                                    >
                                        <option value="" disabled>— Select control mode —</option>
                                        {controlModes.map((m) => (
                                            <option key={m.id} value={m.id}>{m.mode_name} ({(m.gate_sequence || []).length} gates)</option>
                                        ))}
                                    </select>
                                </Field>
                            </>
                        ) : (
                            <Field label={<>Note <span className="text-signal">*</span></>}>
                                <textarea
                                    data-testid="action-note-input"
                                    rows={3}
                                    autoFocus
                                    value={actionNote}
                                    onChange={(e) => setActionNote(e.target.value)}
                                    className="cerb-input"
                                    maxLength={1000}
                                    placeholder={
                                        actionTarget.kind === "info"
                                            ? "What additional details do you need from the submitter?"
                                            : "Why is this idea being archived?"
                                    }
                                />
                                <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider flex justify-between">
                                    <span>Required • visible to submitter</span>
                                    <span className="tabular-nums">{actionNote.length}/1000</span>
                                </div>
                            </Field>
                        )}

                        {modalErr && <div className="text-red-400 text-xs font-mono" data-testid="action-error">{modalErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={closeAction} className="cerb-btn-secondary" data-testid="action-cancel">Cancel</button>
                            <button
                                type="submit"
                                disabled={busy || (actionTarget.kind !== "promote" && actionNote.trim().length < 3)}
                                className={
                                    actionTarget.kind === "promote"
                                        ? "px-6 py-2 font-bold uppercase tracking-[0.2em] text-xs bg-signal text-jet hover:bg-signal-hover transition-colors flex items-center gap-2"
                                        : "cerb-btn-primary"
                                }
                                data-testid="action-confirm"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                                    actionTarget.kind === "info" ? "Send Request"
                                    : actionTarget.kind === "archive" ? "Archive"
                                    : <><Rocket className="w-3 h-3" /> Promote to Product</>}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}

// ---------- sub-components ----------

function IdeaCard({ idea, canReview, onAction, onOpenDetail }) {
    const isFinal = idea.idea_status === "Approved" || idea.idea_status === "Archived";
    const notesCount = idea.discussion_notes?.length || 0;
    return (
        <div
            data-testid={`idea-card-${idea.idea_id}`}
            onClick={(e) => {
                // Don't open detail when an action button (or link inside attachments) is clicked.
                if (e.target.closest("a,button")) return;
                onOpenDetail?.(idea);
            }}
            className={`border bg-jet-elev1 p-5 flex flex-col gap-3 cursor-pointer hover:border-signal/60 transition-colors ${
                idea.idea_status === "Submitted" ? "border-signal/40" : "border-[#2A2A2A]"
            }`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-signal tabular-nums" data-testid={`idea-id-${idea.idea_id}`}>{idea.idea_id}</span>
                    <IdeaStatusBadge status={idea.idea_status} />
                    {notesCount > 0 && (
                        <span
                            className="inline-flex items-center gap-1 border border-[#2A2A2A] text-snow-dim px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em]"
                            data-testid={`idea-notes-count-${idea.idea_id}`}
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-signal animate-pulse-signal" />
                            <MessageSquare className="w-2.5 h-2.5" /> {notesCount}
                        </span>
                    )}
                </div>
                <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider tabular-nums">
                    {relativeTime(idea.created_at)} · {idea.submitter_email}
                </span>
            </div>
            <div className="heading-display text-lg">{idea.title}</div>
            <div className="text-sm text-snow-dim font-mono">{idea.description}</div>
            <div className="text-[11px] text-snow-muted font-mono leading-relaxed">
                <span className="overline mr-2">Impact:</span>{idea.mission_impact}
            </div>
            {(idea.file_attachments?.length > 0 || idea.attachments?.length > 0 || idea.external_links?.length > 0) && (
                <div className="space-y-2">
                    {/* Binary file attachments */}
                    {idea.file_attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 text-[11px]">
                            {idea.file_attachments.map((f) => (
                                <a
                                    key={f.file_id}
                                    href={buildFileDownloadUrl(f.file_id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 border border-signal/40 text-signal/90 bg-signal/5 px-2 py-1 font-mono hover:bg-signal/10 transition-colors"
                                    data-testid={`idea-file-${idea.idea_id}-${f.file_id}`}
                                >
                                    <Paperclip className="w-3 h-3" />
                                    <span className="truncate max-w-[200px]">{f.filename}</span>
                                    <span className="text-[10px] text-snow-muted tabular-nums">({formatBytes(f.size)})</span>
                                </a>
                            ))}
                        </div>
                    )}
                    {/* Legacy + new external_links */}
                    {(idea.external_links?.length > 0 || idea.attachments?.length > 0) && (
                        <div className="flex flex-wrap gap-2 text-[11px]">
                            {(idea.external_links || idea.attachments || []).map((url, idx) => (
                                <a
                                    key={idx}
                                    href={normalizeUrlClient(url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 border border-[#2A2A2A] text-snow-dim px-2 py-1 font-mono hover:border-snow hover:text-snow transition-colors"
                                    data-testid={`idea-link-${idea.idea_id}-${idx}`}
                                >
                                    {url.match(/\.pdf$/i) ? <FileText className="w-3 h-3" /> : <LinkIcon className="w-3 h-3" />}
                                    <span className="truncate max-w-[200px]">{url}</span>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {idea.review_notes?.length > 0 && (
                <div className="border-t border-[#2A2A2A] pt-2 space-y-1">
                    <div className="overline">Review Trail</div>
                    {idea.review_notes.map((n, idx) => (
                        <div key={idx} className="text-[11px] text-snow-muted font-mono">
                            <span className="text-snow-dim">{n.author}:</span> "{n.note}"
                        </div>
                    ))}
                </div>
            )}
            {idea.promoted_product_id && (
                <Link
                    to={`/products/${idea.promoted_product_id}`}
                    data-testid={`idea-product-link-${idea.idea_id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-signal hover:underline self-start"
                >
                    <Rocket className="w-3 h-3" /> Open Product →
                </Link>
            )}

            {canReview && !isFinal && (
                <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-[#2A2A2A]">
                    <button
                        data-testid={`request-info-${idea.idea_id}`}
                        onClick={() => onAction("info", idea)}
                        className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-snow hover:text-snow transition-colors flex items-center gap-1"
                    >
                        <MessageSquare className="w-3 h-3" /> Request Info
                    </button>
                    <button
                        data-testid={`archive-${idea.idea_id}`}
                        onClick={() => onAction("archive", idea)}
                        className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-red-500 hover:text-red-400 transition-colors flex items-center gap-1"
                    >
                        <ArchiveIcon className="w-3 h-3" /> Archive
                    </button>
                    {/* Spec: Accent Orange (#FF6A00) for Promote — major state transition. */}
                    <button
                        data-testid={`promote-${idea.idea_id}`}
                        onClick={() => onAction("promote", idea)}
                        className="ml-auto px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] bg-signal text-jet hover:bg-signal-hover transition-colors flex items-center gap-2"
                    >
                        <Rocket className="w-3 h-3" /> Promote to Product
                    </button>
                </div>
            )}
        </div>
    );
}

function ConceptDetailModal({ idea, onClose, onAction, canReview, canAppendAssets, isSubmitter, onIdeaUpdated }) {
    const [noteText, setNoteText] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [linkUrl, setLinkUrl] = useState("");
    const [linkBusy, setLinkBusy] = useState(false);
    const [linkErr, setLinkErr] = useState("");
    const isFinal = idea.idea_status === "Approved" || idea.idea_status === "Archived";
    // Build Prompt #17 — broad collab: anyone with append_concept_assets, OR leadership,
    // OR the submitter while still in Submitted, may append assets. Locked once Approved/Archived.
    const canAppendNow = !isFinal && (canReview || canAppendAssets || (isSubmitter && idea.idea_status === "Submitted"));

    const submitNote = async (e) => {
        e.preventDefault();
        const text = noteText.trim();
        if (text.length < 1) return;
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post(`/product-ideas/${idea.idea_id}/discussion-notes`, { text });
            setNoteText("");
            onIdeaUpdated?.(data);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2?.message);
        } finally {
            setBusy(false);
        }
    };

    const submitLink = async (e) => {
        e.preventDefault();
        const raw = linkUrl.trim();
        if (!raw) return;
        // Build Prompt #18 — normalize client-side too so the user sees the final
        // form. Backend re-validates regardless, but this avoids a round-trip 400.
        const url = normalizeUrlClient(raw);
        setLinkBusy(true);
        setLinkErr("");
        try {
            const { data } = await api.post(`/product-ideas/${idea.idea_id}/external-links`, { url });
            setLinkUrl("");
            onIdeaUpdated?.(data);
            toast.success("Link appended to concept");
        } catch (e2) {
            setLinkErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2?.message);
        } finally {
            setLinkBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-jet/85 backdrop-blur-sm p-4"
            data-testid="concept-detail-modal"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-3xl bg-jet border border-hairline max-h-[92vh] overflow-y-auto shadow-[0_0_60px_rgba(255,106,0,0.10)]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between border-b border-hairline px-6 py-4 sticky top-0 bg-jet z-10">
                    <div className="min-w-0">
                        <div className="overline flex items-center gap-2">
                            <Lightbulb className="w-3 h-3 text-signal" /> Concept Detail
                        </div>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <span className="font-mono text-sm text-signal tabular-nums" data-testid="detail-idea-id">{idea.idea_id}</span>
                            <IdeaStatusBadge status={idea.idea_status} />
                            <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider tabular-nums">
                                {relativeTime(idea.created_at)} · {idea.submitter_email}
                            </span>
                        </div>
                        <div className="heading-display text-2xl mt-2">{idea.title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal transition-colors" data-testid="detail-close-btn">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    <section>
                        <div className="overline mb-2">Description</div>
                        <div className="text-sm text-snow leading-relaxed whitespace-pre-wrap" data-testid="detail-description">{idea.description}</div>
                    </section>

                    <section>
                        <div className="overline mb-2">Mission Impact</div>
                        <div className="text-sm text-snow-dim leading-relaxed whitespace-pre-wrap" data-testid="detail-impact">{idea.mission_impact}</div>
                    </section>

                    {/* === Technical Assets (Build Prompt #17) === */}
                    <section data-testid="concept-assets-section">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                                <Paperclip className="w-3.5 h-3.5 text-signal" />
                                <div className="overline">Technical Assets</div>
                                <span className="text-[10px] font-mono text-snow-muted tabular-nums" data-testid="assets-count">
                                    {(idea.file_attachments?.length || 0)} file{(idea.file_attachments?.length || 0) === 1 ? "" : "s"} · {(idea.external_links?.length || 0)} link{(idea.external_links?.length || 0) === 1 ? "" : "s"}
                                </span>
                            </div>
                            {isFinal && (
                                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-snow-muted border border-[#2A2A2A] px-2 py-0.5">
                                    Locked — {idea.idea_status}
                                </span>
                            )}
                        </div>

                        {/* Gallery — file thumbnails / icons */}
                        {(idea.file_attachments?.length > 0) ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3" data-testid="concept-asset-gallery">
                                {idea.file_attachments.map((f) => (
                                    <AssetTile key={f.file_id} file={f} />
                                ))}
                            </div>
                        ) : (
                            <div className="border border-[#2A2A2A] bg-jet-elev1 px-4 py-5 text-center text-snow-muted text-[11px] font-mono mb-3" data-testid="concept-no-files">
                                No files attached yet.
                            </div>
                        )}

                        {/* External links — high-vis */}
                        {(idea.external_links?.length > 0) && (
                            <div className="space-y-1.5 mb-3" data-testid="concept-external-links">
                                {idea.external_links.map((url, idx) => (
                                    <a
                                        key={idx}
                                        href={normalizeUrlClient(url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`concept-external-link-${idx}`}
                                        className="flex items-center gap-2 border border-signal/40 text-signal bg-signal/5 hover:bg-signal/10 px-3 py-2 font-mono text-[11px] transition-colors"
                                    >
                                        <LinkIcon className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate flex-1">{url}</span>
                                        <span className="text-[9px] text-snow-muted uppercase tracking-wider">Open ↗</span>
                                    </a>
                                ))}
                            </div>
                        )}

                        {/* Append controls — broad collab gating */}
                        {canAppendNow && (
                            <div className="border border-[#2A2A2A] bg-jet-elev1 p-3 space-y-3" data-testid="concept-append-panel">
                                <div className="flex items-center gap-2">
                                    <Plus className="w-3 h-3 text-signal" />
                                    <div className="overline">Append New Assets</div>
                                </div>
                                <FileDropzone
                                    mode="attach"
                                    entityUrl={`/product-ideas/${idea.idea_id}/files`}
                                    value={idea.file_attachments || []}
                                    onChange={(nextAttachments) => onIdeaUpdated?.({ ...idea, file_attachments: nextAttachments })}
                                    testIdPrefix="concept-dropzone"
                                    label="Drop spec sheets, images, or docs to attach"
                                    helpText="Max 25 MB per file · 10 files / 50 MB total. Appending an asset is audit-logged."
                                />
                                <form onSubmit={submitLink} className="flex flex-col sm:flex-row gap-2" data-testid="concept-add-link-form">
                                    <input
                                        type="text"
                                        inputMode="url"
                                        value={linkUrl}
                                        onChange={(e) => setLinkUrl(e.target.value)}
                                        placeholder="docs.example.com/spec.pdf or https://…"
                                        className="cerb-input flex-1"
                                        disabled={linkBusy}
                                        data-testid="concept-link-input"
                                    />
                                    <button
                                        type="submit"
                                        disabled={linkBusy || linkUrl.trim().length < 4}
                                        className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] border border-signal text-signal hover:bg-signal hover:text-jet transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid="concept-add-link-btn"
                                    >
                                        {linkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />}
                                        Add Link
                                    </button>
                                </form>
                                {linkErr && (
                                    <div className="text-[11px] text-red-400 font-mono" data-testid="concept-link-error">{linkErr}</div>
                                )}
                            </div>
                        )}

                        {!canAppendNow && !isFinal && (
                            <div className="text-[10px] text-snow-muted font-mono italic" data-testid="concept-append-disabled">
                                Your role doesn't permit appending assets to other concepts.
                            </div>
                        )}
                    </section>

                    {idea.review_notes?.length > 0 && (
                        <section>
                            <div className="overline mb-2">Review Trail (Leadership)</div>
                            <ul className="space-y-1.5">
                                {idea.review_notes.map((n, idx) => (
                                    <li key={idx} className="text-[11px] text-snow-muted font-mono">
                                        <span className="text-snow-dim">{n.author}:</span> "{n.note}"
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {idea.promoted_product_id && (
                        <Link
                            to={`/products/${idea.promoted_product_id}`}
                            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-signal border border-signal/40 hover:bg-signal hover:text-jet px-3 py-1.5 transition-colors"
                            data-testid="detail-product-link"
                        >
                            <Rocket className="w-3 h-3" /> Promoted to Product → Open
                        </Link>
                    )}

                    {/* === Notes & Discussion === Build Prompt #16 */}
                    <section className="border-t border-[#2A2A2A] pt-5">
                        <div className="flex items-center gap-2 mb-3">
                            <MessageSquare className="w-3.5 h-3.5 text-signal" />
                            <div className="overline">Notes & Discussion</div>
                            <span className="text-[10px] font-mono text-snow-muted tabular-nums" data-testid="discussion-count">
                                {idea.discussion_notes?.length || 0} note{(idea.discussion_notes?.length || 0) === 1 ? "" : "s"}
                            </span>
                        </div>

                        {(idea.discussion_notes?.length || 0) === 0 ? (
                            <div className="border border-[#2A2A2A] bg-jet-elev1 px-4 py-6 text-center text-snow-muted text-xs font-mono" data-testid="discussion-empty">
                                No notes yet. Start the conversation below.
                            </div>
                        ) : (
                            <ul className="space-y-2" data-testid="discussion-list">
                                {idea.discussion_notes.map((n) => (
                                    <li
                                        key={n.id}
                                        data-testid={`discussion-note-${n.id}`}
                                        className="bg-[#2A2A2A] border border-[#2A2A2A] px-4 py-3 text-snow"
                                    >
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="font-mono text-[11px] text-signal font-bold">{n.author_name}</span>
                                            <span className="text-[10px] text-snow-muted font-mono tabular-nums">
                                                {relativeTime(n.created_at)}
                                            </span>
                                        </div>
                                        <div className="text-sm font-mono leading-relaxed whitespace-pre-wrap">{n.text}</div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* New-note form */}
                        <form onSubmit={submitNote} className="mt-3" data-testid="discussion-form">
                            <textarea
                                data-testid="discussion-note-input"
                                rows={2}
                                maxLength={2000}
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                className="cerb-input w-full"
                                placeholder="Add a note to the discussion…"
                                disabled={busy}
                            />
                            {err && <div className="text-[11px] text-red-400 font-mono mt-1.5">{err}</div>}
                            <div className="flex items-center justify-between gap-3 mt-2">
                                <div className="text-[10px] text-snow-muted font-mono">
                                    Notes persist even after the idea is promoted.
                                </div>
                                <button
                                    type="submit"
                                    disabled={busy || noteText.trim().length < 1}
                                    className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] bg-signal text-jet hover:bg-signal-hover transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="discussion-submit-btn"
                                >
                                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                                    Post Note
                                </button>
                            </div>
                        </form>
                    </section>

                    {/* Leadership actions */}
                    {canReview && !isFinal && (
                        <section className="border-t border-[#2A2A2A] pt-5 flex flex-wrap items-center gap-2">
                            <button
                                onClick={() => onAction("info", idea)}
                                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-snow hover:text-snow transition-colors flex items-center gap-1"
                                data-testid="detail-request-info-btn"
                            >
                                <MessageSquare className="w-3 h-3" /> Request Info
                            </button>
                            <button
                                onClick={() => onAction("archive", idea)}
                                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-red-500 hover:text-red-400 transition-colors flex items-center gap-1"
                                data-testid="detail-archive-btn"
                            >
                                <ArchiveIcon className="w-3 h-3" /> Archive
                            </button>
                            <button
                                onClick={() => onAction("promote", idea)}
                                className="ml-auto px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] bg-signal text-jet hover:bg-signal-hover transition-colors flex items-center gap-2"
                                data-testid="detail-promote-btn"
                            >
                                <Rocket className="w-3 h-3" /> Promote to Product
                            </button>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}

function IdeaStatusBadge({ status }) {
    const map = {
        "Submitted":         "border-signal/60 text-signal bg-signal/5",
        "Under Review":      "border-blue-500/40 text-blue-300 bg-blue-500/5",
        "More Info Needed":  "border-yellow-500/40 text-yellow-300 bg-yellow-500/5",
        "Approved":          "border-green-500/40 text-green-400 bg-green-500/5",
        "Archived":          "border-[#2A2A2A] text-snow-muted bg-jet-elev2",
    };
    return (
        <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${map[status] || "border-[#2A2A2A] text-snow-dim"}`}>
            {status}
        </span>
    );
}

function KPI({ label, value, alert, icon: Icon, testid }) {
    return (
        <div
            data-testid={testid}
            className={`border ${alert ? "border-signal bg-signal/5" : "border-[#2A2A2A] bg-jet-elev1"} p-4`}
        >
            <div className="flex items-center justify-between">
                <div className="overline">{label}</div>
                {Icon && <Icon className={`w-4 h-4 ${alert ? "text-signal" : "text-snow-muted"}`} />}
            </div>
            <div className={`heading-display text-3xl mt-2 tabular-nums ${alert ? "text-signal" : "text-snow"}`}>
                {value || 0}
            </div>
        </div>
    );
}

function SegmentedControl({ name, options, value, onChange }) {
    return (
        <div className="flex border border-[#2A2A2A]" data-testid={`ci-segmented-${name}`}>
            {options.map((opt) => (
                <button
                    key={opt}
                    data-testid={`ci-${name}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => onChange(opt)}
                    className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                        value === opt
                            ? "bg-signal text-jet"
                            : "text-snow-dim hover:text-snow"
                    }`}
                >
                    {opt}
                </button>
            ))}
        </div>
    );
}

function Modal({ title, children, onClose, accent }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className={`relative w-full max-w-xl bg-jet-elev1 border ${accent ? "border-signal" : "border-hairline"} max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`flex items-center justify-between border-b ${accent ? "border-signal" : "border-hairline"} px-5 py-3`}>
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal" data-testid="modal-close-btn">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className="overline block mb-2">{label}</label>{children}</div>;
}

function relativeTime(iso) {
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch {
        return iso;
    }
}

/**
 * Build Prompt #17 / #18 — Visual tile for a concept's file attachment.
 *  • Images render as thumbnails (loaded from /api/files/{id}?token=…). Click → full-size new tab.
 *  • PDFs render a large document icon + a dedicated "View / Download" button that
 *    opens the file inline in a new browser-native PDF viewer tab.
 *  • Everything else renders the document icon with a Download button.
 *
 *  All links use rel="noopener noreferrer" + target="_blank" and absolute URLs so
 *  React Router never intercepts them.
 */
function AssetTile({ file }) {
    const ct = (file.content_type || "").toLowerCase();
    const isImage = ct.startsWith("image/");
    const isPdf = ct === "application/pdf" || (file.filename || "").toLowerCase().endsWith(".pdf");
    const href = buildFileDownloadUrl(file.file_id);
    const [imgFailed, setImgFailed] = useState(false);
    const showImage = isImage && !imgFailed;
    return (
        <div
            data-testid={`asset-tile-${file.file_id}`}
            className="group block border border-signal/40 bg-jet-elev1 hover:border-signal hover:bg-signal/5 transition-colors overflow-hidden"
        >
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                aria-label={`Open ${file.filename}`}
            >
                <div className="aspect-square flex items-center justify-center bg-jet relative overflow-hidden">
                    {showImage ? (
                        <img
                            src={href}
                            alt={file.filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={() => setImgFailed(true)}
                        />
                    ) : isImage && imgFailed ? (
                        // Build Prompt #19 — tactical "File Missing" state when the
                        // image can't be loaded (404, 401, server-side encoding error, etc.).
                        // Distinct visual from PDF/Doc icons so the operator can tell
                        // the blob is corrupt vs. just not an image.
                        <div className="flex flex-col items-center justify-center text-red-400" data-testid={`asset-missing-${file.file_id}`}>
                            <AlertTriangle className="w-10 h-10 mb-1" />
                            <div className="text-[9px] font-mono uppercase tracking-wider">File Missing</div>
                        </div>
                    ) : isPdf ? (
                        <FileText className="w-12 h-12 text-signal" data-testid={`asset-pdf-icon-${file.file_id}`} />
                    ) : (
                        <FileText className="w-10 h-10 text-signal/80 group-hover:text-signal" />
                    )}
                </div>
                <div className="px-2 py-1.5 border-t border-signal/30">
                    <div className="text-[10px] font-mono text-snow truncate" title={file.filename}>{file.filename}</div>
                    <div className="text-[9px] text-snow-muted font-mono uppercase tracking-wider tabular-nums">
                        {formatBytes(file.size)} {isPdf ? "· PDF" : isImage ? "· IMG" : ""}
                    </div>
                </div>
            </a>
            {/* Dedicated View / Download button per media type. All open native tab. */}
            <div className="px-2 py-1.5 border-t border-signal/30">
                {isPdf ? (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`asset-view-pdf-${file.file_id}`}
                        className="block text-center px-2 py-1 text-[10px] font-mono uppercase tracking-wider bg-signal text-jet hover:bg-signal-hover transition-colors"
                    >
                        View / Download PDF
                    </a>
                ) : (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`asset-open-${file.file_id}`}
                        className="block text-center px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-signal/40 text-signal hover:bg-signal hover:text-jet transition-colors"
                    >
                        {isImage ? "Open Full-Size ↗" : "Download ↗"}
                    </a>
                )}
            </div>
        </div>
    );
}
